/**
 * A name, a glyph and a colour — the three things that make a group or an album
 * findable in a list of twelve.
 *
 * They were three stacked blocks in two different dialogs, each done slightly
 * differently: the album had a grid of twenty-five glyphs and a switch, the
 * group had a free-text box you were expected to paste an emoji into. Both were
 * describing one thing, so this is one control, and both use it.
 *
 * The chip shows the answer; the panel it opens asks the question. Nothing is
 * hidden that has been set — a chosen glyph and colour are visible on the chip
 * without opening anything.
 */

import { useId, useMemo, useRef, useState } from "react";
import { useT } from "../../lib/i18n.js";
import { ACCENT_COLORS } from "@fc/core";
import type { AccentColor } from "@fc/core";
import { EmojiIcon } from "./EmojiIcon.js";
import { Field } from "./Field.js";
import { Input } from "./Input.js";
import {
  searchEmoji,
  suggestEmoji,
  type EmojiEntry,
} from "./emojiCatalogue.js";
// Where the eight label colours' swatch markup lives.
import "../../features/group/GroupSettingsPage.css";
import "./IdentityField.css";

/* ──────────────────────────────── Emoji set ───────────────────────────────── */

interface EmojiOption {
  /** Empty string is the "no emoji" choice. */
  emoji: string;
  label: string;
}

/**
 * What you see before typing anything.
 *
 * Curated because a 3,000-glyph wall is a worse answer to "which one says
 * holiday" than two dozen options. It is a poor set to *find* in, though —
 * people name albums after where they went, and no amount of scrolling these
 * produces a Spanish flag — so a search box sits above it and reaches the full
 * catalogue, flags included. Every one is a single grapheme, which is what the
 * server's `emojiSchema` insists on.
 */
export const EMOJI_OPTIONS: readonly EmojiOption[] = [
  { emoji: "", label: "No emoji" },
  { emoji: "✈️", label: "Flight" },
  { emoji: "🏖", label: "Beach" },
  { emoji: "🏔", label: "Mountains" },
  { emoji: "🎉", label: "Party" },
  { emoji: "🎂", label: "Birthday" },
  { emoji: "🍕", label: "Food" },
  { emoji: "🌊", label: "Sea" },
  { emoji: "🏕", label: "Camping" },
  { emoji: "🎿", label: "Skiing" },
  { emoji: "🚗", label: "Road trip" },
  { emoji: "🚲", label: "Cycling" },
  { emoji: "🌅", label: "Sunrise" },
  { emoji: "🌸", label: "Spring" },
  { emoji: "🎄", label: "Christmas" },
  { emoji: "💍", label: "Wedding" },
  { emoji: "👶", label: "New baby" },
  { emoji: "🐶", label: "Pets" },
  { emoji: "🎸", label: "Music" },
  { emoji: "⛺", label: "Festival" },
  { emoji: "🗺", label: "Exploring" },
  { emoji: "🏛", label: "Sightseeing" },
  { emoji: "🚂", label: "Train journey" },
  { emoji: "🍷", label: "Drinks" },
  { emoji: "📸", label: "Photos" },
];

export interface IdentityFieldProps {
  name: string;
  onNameChange: (value: string) => void;
  nameLabel?: string;
  namePlaceholder?: string;
  nameError?: string;
  nameMaxLength?: number;
  nameRef?: React.RefObject<HTMLInputElement | null>;

  emoji: string;
  onEmojiChange: (value: string) => void;

  /** Null means "follow the group", offered only when `allowInherit`. */
  colour: AccentColor | null;
  onColourChange: (value: AccentColor | null) => void;
  /**
   * Whether "follow the group" is one of the choices. False for a group, which
   * has no group to follow.
   */
  allowInherit?: boolean;

  disabled?: boolean;
}


export function IdentityField({
  name,
  onNameChange,
  nameLabel = "Name",
  namePlaceholder,
  nameError,
  nameMaxLength = 80,
  nameRef,
  emoji,
  onEmojiChange,
  colour,
  onColourChange,
  allowInherit = false,
  disabled = false,
}: IdentityFieldProps) {
  const [open, setOpen] = useState(false);

  /*
   * Recomputed as the name is typed. Cheap — a couple of hundred keyword
   * comparisons — and doing it on every keystroke is what makes it feel like
   * the field understood, rather than like a button that appeared late.
   */
  const suggestion = useMemo(() => suggestEmoji(name), [name]);

  return (
    <div className="fc-identity">
      <div className="fc-identity__row">
        <button
          type="button"
          className="fc-identity__chip"
          data-label={colour ?? undefined}
          data-open={open || undefined}
          aria-expanded={open}
          aria-label={
            emoji
              ? `Icon ${emoji}. Change icon and colour`
              : "Choose an icon and colour"
          }
          disabled={disabled}
          onClick={() => setOpen((current) => !current)}
        >
          {emoji ? (
            <span className="fc-identity__glyph" aria-hidden="true">
              <EmojiIcon emoji={emoji} />
            </span>
          ) : (
            <svg
              className="fc-identity__empty"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M4 7.5A2.5 2.5 0 0 1 6.5 5h3l2 2.5h6A2.5 2.5 0 0 1 20 10v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
            </svg>
          )}
          <span className="fc-identity__pencil" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17z" />
            </svg>
          </span>
        </button>

        <div className="fc-identity__name">
          <Field label={nameLabel} required error={nameError}>
            <Input
              ref={nameRef}
              value={name}
              maxLength={nameMaxLength}
              autoComplete="off"
              disabled={disabled}
              onChange={(event) => onNameChange(event.target.value)}
              placeholder={namePlaceholder}
            />
          </Field>
        </div>
      </div>

      {/*
        Offered, never applied. Guessing from a name is right often enough to
        save a trip to the picker and wrong often enough that doing it silently
        would be worse than not guessing — "Chris's leaving do" is not a
        Christmas album. Only shown while nothing has been chosen, so it can
        never argue with a decision somebody has already made.
      */}
      {suggestion && !emoji && !disabled && (
        <button
          type="button"
          className="fc-identity__suggestion"
          onClick={() => onEmojiChange(suggestion.emoji)}
        >
          <span aria-hidden="true">
            <EmojiIcon emoji={suggestion.emoji} />
          </span>
          Use {suggestion.label}?
        </button>
      )}

      {open && (
        <div className="fc-identity__panel">
          <ColourRow
            value={colour}
            onChange={onColourChange}
            allowInherit={allowInherit}
            disabled={disabled}
          />
          <EmojiRow
            value={emoji}
            onChange={onEmojiChange}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────── Colour picker ─────────────────────────────── */

function ColourRow({
  value,
  onChange,
  allowInherit,
  disabled,
}: {
  value: AccentColor | null;
  onChange: (value: AccentColor | null) => void;
  allowInherit: boolean;
  disabled: boolean;
}) {
  const t = useT();
  const labelId = useId();
  const options: (AccentColor | null)[] = allowInherit
    ? [null, ...ACCENT_COLORS]
    : [...ACCENT_COLORS];

  /*
   * `fc-accents` is the wrapper the eight colour tokens' swatch styles hang
   * off, so using it is not cosmetic: with any other wrapper the swatches
   * inherit no palette and render as empty circles.
   */
  return (
    <div className="fc-accents">
      <span className="fc-accents__label" id={labelId}>
        {t("Colour")}
      </span>
      <div
        className="fc-accents__row"
        role="radiogroup"
        aria-labelledby={labelId}
      >
        {options.map((option) => (
          <button
            key={option ?? "inherit"}
            type="button"
            role="radio"
            aria-checked={option === value}
            aria-label={option === null ? "Follow the group's colour" : option}
            title={option === null ? "Follow the group" : option}
            disabled={disabled}
            className="fc-accents__swatch"
            data-color={option ?? undefined}
            data-inherit={option === null ? "" : undefined}
            onClick={() => onChange(option)}
          >
            {option === null ? (
              <svg
                className="fc-identity__inherit-icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            ) : (
              <span className="fc-accents__dot" aria-hidden="true" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────── Emoji picker ─────────────────────────────── */

/**
 * The ARIA radio-group pattern: one tab stop for the whole grid, arrows move
 * and select. Tabbing through twenty-five glyphs to reach the next field would
 * be punishing for anyone on a keyboard. Arrows are one linear sequence rather
 * than a 2-D grid, because the column count is set by however much room there
 * happens to be.
 */
function EmojiRow({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const t = useT();
  const labelId = useId();
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const [query, setQuery] = useState("");

  /*
   * Typing replaces the curated grid rather than filtering it: the curated set
   * is what to show somebody with no particular idea, and a search is the
   * opposite — a person who knows exactly what they want and needs it to be
   * reachable, which for a flag means one of nearly two hundred.
   */
  const results = useMemo<readonly EmojiOption[]>(() => {
    const found = searchEmoji(query);
    if (found.length === 0) return [];
    return found.map((entry: EmojiEntry) => ({
      emoji: entry.emoji,
      label: entry.label,
    }));
  }, [query]);

  const searching = query.trim().length > 0;
  const shown: readonly EmojiOption[] = searching ? results : EMOJI_OPTIONS;

  const activeIndex = Math.max(
    0,
    shown.findIndex((option) => option.emoji === value),
  );

  const select = (index: number) => {
    const count = shown.length;
    if (count === 0) return;
    const next = shown[((index % count) + count) % count];
    if (next === undefined) return;
    buttons.current[shown.indexOf(next)]?.focus();
    if (next.emoji !== value) onChange(next.emoji);
  };

  const onKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        select(index + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        select(index - 1);
        break;
      case "Home":
        event.preventDefault();
        select(0);
        break;
      case "End":
        event.preventDefault();
        select(shown.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <div className="fc-identity__emoji">
      <span className="fc-accents__label" id={labelId}>
        {t("Emoji")}
      </span>

      <input
        type="search"
        className="fc-identity__emoji-search"
        value={query}
        disabled={disabled}
        placeholder={t("Search — a country, a flag, a mood")}
        aria-label={t("Search emoji")}
        onChange={(event) => setQuery(event.target.value)}
      />

      {/*
        Said once, where it would otherwise look like a bug.

        Windows has no artwork for flag emoji, so a perfectly valid Spanish flag
        renders as the letters "ES". The character saved is correct and shows as
        a flag on a phone — but a picker full of two-letter codes looks broken,
        and being told why is the difference between a limitation and a fault.
      */}
      {searching && shown.length === 0 && (
        <p className="fc-identity__emoji-empty">
          Nothing matches &ldquo;{query.trim()}&rdquo;.
        </p>
      )}

      {/*
        Search results are named; the curated grid is not.

        Two dozen familiar pictures need no captions. A hundred and eighty flags
        do — several are near-identical at this size, and on Windows there are
        no flag glyphs at all, so the row renders as "AF AL DZ AD" and is
        unusable without the country beside it. The name is the only part of a
        flag that is portable across platforms.
      */}
      <div
        className="fc-identity__emoji-grid"
        data-named={searching || undefined}
        role="radiogroup"
        aria-labelledby={labelId}
      >
        {shown.map((option, index) => (
          <button
            key={option.emoji || option.label}
            ref={(node) => {
              buttons.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={option.emoji === value}
            aria-label={option.label}
            title={option.label}
            tabIndex={index === activeIndex ? 0 : -1}
            disabled={disabled}
            className="fc-identity__emoji-option"
            onClick={() => select(index)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {option.emoji === "" ? (
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
                className="fc-identity__clear"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            ) : (
              <span aria-hidden="true">
                <EmojiIcon emoji={option.emoji} />
              </span>
            )}
            {searching && option.emoji !== "" && (
              <span className="fc-identity__emoji-caption" aria-hidden="true">
                {option.label}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
